import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { DiscountType, PromoCode } from "@/core/entities/promo-code";
import { ProductType } from "@/core/entities/product";
import { useUserStore } from "@/stores/user";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
  VisibilityState,
} from "@tanstack/react-table";
import { Edit, Eye, MoreHorizontal, Settings2, Trash2 } from "lucide-react";
import { useState } from "react";

interface PromoCodesTableProps {
  promoCodes: PromoCode[];
  onView: (promoCode: PromoCode) => void;
  onEdit: (promoCode: PromoCode) => void;
  onDelete: (promoCodeId: string) => void;
}

export function PromoCodesTable({
  promoCodes,
  onView,
  onEdit,
  onDelete,
}: Readonly<PromoCodesTableProps>) {
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({
    code: true,
    name: true,
    discount: true,
    enabledFor: true,
    expirationDate: true,
    updatedAt: true,
    actions: true,
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [promoCodeToDelete, setPromoCodeToDelete] = useState<PromoCode | null>(null);

  const { hasFullAccess } = useUserStore();

  const handleDeleteClick = (promoCode: PromoCode) => {
    setPromoCodeToDelete(promoCode);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (promoCodeToDelete) {
      onDelete(promoCodeToDelete.id);
      setDeleteDialogOpen(false);
      setPromoCodeToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setPromoCodeToDelete(null);
  };

  const formatDiscount = (promoCode: PromoCode) => {
    if (promoCode.discount.type === DiscountType.PERCENTAGE) {
      return `${promoCode.discount.value}%`;
    }
    return `$${promoCode.discount.value.toFixed(2)}`;
  };

  const formatEnabledFor = (enabledFor: ProductType) => {
    const labels: Record<ProductType, string> = {
      [ProductType.BUY]: "Buy",
      [ProductType.SELL]: "Sell",
      [ProductType.BOTH]: "Both",
    };
    return labels[enabledFor];
  };

  const getEnabledForBadge = (enabledFor: ProductType) => {
    const variants = {
      [ProductType.BUY]: "bg-secondary text-secondary-foreground hover:bg-secondary",
      [ProductType.SELL]: "bg-secondary text-secondary-foreground hover:bg-muted",
      [ProductType.BOTH]: "bg-primary/10 text-primary hover:bg-primary/10",
    };
    return <Badge className={variants[enabledFor]}>{formatEnabledFor(enabledFor)}</Badge>;
  };

  const isExpired = (promoCode: PromoCode) => {
    if (!promoCode.expirationDate) return false;
    return new Date(promoCode.expirationDate) < new Date();
  };

  const columns: ColumnDef<PromoCode>[] = [
    {
      id: "code",
      accessorKey: "code",
      header: "Code",
      cell: ({ row }) => {
        return (
          <div className="font-mono font-semibold">{row.original.code}</div>
        );
      },
    },
    {
      id: "name",
      accessorKey: "name",
      header: "Name",
      cell: ({ row }) => {
        return <div className="font-medium">{row.original.name}</div>;
      },
    },
    {
      id: "discount",
      accessorKey: "discount",
      header: "Discount",
      cell: ({ row }) => {
        return (
          <div className="text-sm">
            {formatDiscount(row.original)}
          </div>
        );
      },
    },
    {
      id: "enabledFor",
      accessorKey: "enabledFor",
      header: "Enabled For",
      cell: ({ row }) => {
        return getEnabledForBadge(row.original.enabledFor);
      },
    },
    {
      id: "expirationDate",
      accessorKey: "expirationDate",
      header: "Expiration",
      cell: ({ row }) => {
        const promoCode = row.original;
        if (!promoCode.expirationDate) {
          return <span className="text-sm text-muted-foreground">No expiration</span>;
        }
        const expired = isExpired(promoCode);
        return (
          <div className="text-sm">
            <div>{new Date(promoCode.expirationDate).toLocaleDateString()}</div>
            {expired && (
              <Badge variant="destructive" className="mt-1">
                Expired
              </Badge>
            )}
          </div>
        );
      },
    },
    {
      id: "updatedAt",
      accessorKey: "updatedAt",
      header: "Last Updated",
      cell: ({ row }) => {
        return new Date(row.original.updatedAt).toLocaleDateString();
      },
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) => {
        const promoCode = row.original;
        return (
          <div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="h-8 w-8 p-0">
                  <span className="sr-only">Open menu</span>
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Actions</DropdownMenuLabel>
                <DropdownMenuItem onClick={() => onView(promoCode)}>
                  <Eye className="mr-2 h-4 w-4" />
                  View Details
                </DropdownMenuItem>
                {hasFullAccess() && (
                  <>
                    <DropdownMenuItem onClick={() => onEdit(promoCode)}>
                      <Edit className="mr-2 h-4 w-4" />
                      Edit Promo Code
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => handleDeleteClick(promoCode)}
                      className="text-red-600"
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
    },
  ];

  const table = useReactTable({
    data: promoCodes,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    state: {
      columnVisibility,
    },
  });

  return (
    <>
      <div className="flex flex-col gap-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="ml-auto py-2">
              <Settings2 className="h-4 w-4" />
              View
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Toggle columns</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {table
              .getAllColumns()
              .filter((column) => column.getCanHide())
              .map((column) => {
                return (
                  <DropdownMenuCheckboxItem
                    key={column.id}
                    className="capitalize"
                    checked={column.getIsVisible()}
                    onCheckedChange={(value) =>
                      column.toggleVisibility(!!value)
                    }
                  >
                    {column.id}
                  </DropdownMenuCheckboxItem>
                );
              })}
          </DropdownMenuContent>
        </DropdownMenu>
        <div className="rounded-md border overflow-hidden overflow-x-auto">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id} className="border-b">
                  {headerGroup.headers.map((header) => {
                    return (
                      <TableHead
                        key={header.id}
                        className={`relative group whitespace-nowrap ${header.column.id === "actions" ? "text-center w-12" : "text-left px-2"} border-r last:border-r-0`}
                      >
                        {header.isPlaceholder ? null : (
                          <div
                            className={`flex items-center ${header.column.id === "actions" ? "justify-center" : "justify-start"}`}
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                          </div>
                        )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && "selected"}
                    className="hover:bg-muted/50 border-b"
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        key={cell.id}
                        className={`whitespace-nowrap ${cell.column.id === "actions" ? "text-center w-12" : "text-left"} border-r last:border-r-0`}
                      >
                        {cell.column.id === "actions" ? (
                          <div className="flex items-center justify-center">
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext(),
                            )}
                          </div>
                        ) : (
                          flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    No promo codes found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Promo Code</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{promoCodeToDelete?.code}"? This
              action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleDeleteCancel}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
